import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import useCompanyProfileCompletionState from './completion.state';

export interface IOrganization {
    id: number;
    name: string;
    description?: string;
    tenant_id: number;
    is_published: boolean;
    created_at?: string;
    updated_at?: string;
}

interface OrganizationListState {
    organizations: IOrganization[];
    isLoading: boolean;
    error: unknown;

    fetchOrganizations: (tenantId: string | number) => Promise<void>;
    fetchOrganization: (id: string | number) => Promise<IOrganization | null>;
    createOrganization: (tenantId: string | number, data: Partial<IOrganization>) => Promise<void>;
    updateOrganization: (id: number, data: Partial<IOrganization>) => Promise<void>;
    deleteOrganization: (id: number) => Promise<void>;
    setPublished: (id: number, tenantId: number, isPublished: boolean) => Promise<void>;
}

const useOrganizationListState = create<OrganizationListState>((set, get) => ({
    organizations: [],
    isLoading: false,
    error: null,

    fetchOrganizations: async (tenantId) => {
        set({ isLoading: true });
        try {
            const url = `${env.apiTasklistBaseUrl}/api/v1/compro/organizations/tenant/${tenantId}`;
            const { data } = await axiosInstance.get(url);
            set({ organizations: data.data.list, isLoading: false, error: null });
        } catch (error) {
            errorHandler(error);
            set({ organizations: [], isLoading: false, error });
        }
    },

    fetchOrganization: async (id) => {
        set({ isLoading: true });
        try {
            const url = `${env.apiTasklistBaseUrl}/api/v1/compro/organizations/${id}`;
            const { data } = await axiosInstance.get(url);
            set({ isLoading: false, error: null });
            return data.data;
        } catch (error) {
            errorHandler(error);
            set({ isLoading: false, error });
            return null;
        }
    },

    createOrganization: async (tenantId, data) => {
        set({ isLoading: true });
        try {
            const payload = {
                ...data,
                tenant_id: Number(tenantId),
            };
            await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/organizations/tenant/${tenantId}`, payload);
            const completionStore = useCompanyProfileCompletionState.getState();
            completionStore.fetchCompletion();
            toast.success('Organization created successfully');
            // Refresh list
            await get().fetchOrganizations(tenantId);
        } catch (error) {
            errorHandler(error);
            set({ isLoading: false, error });
        }
    },

    updateOrganization: async (id, data) => {
        set({ isLoading: true });
        try {
            await axiosInstance.put(`${env.apiTasklistBaseUrl}/api/v1/compro/organizations/${id}`, data);
            toast.success('Organization updated successfully');
            // Re-fetch to ensure the list is consistently sorted by name after change
            const tenantId = get().organizations.find(o => o.id === id)?.tenant_id;
            if (tenantId) {
                await get().fetchOrganizations(tenantId);
            }
            set({ isLoading: false });
        } catch (error) {
            errorHandler(error);
            set({ isLoading: false, error });
        }
    },

    deleteOrganization: async (id) => {
        set({ isLoading: true });
        try {
            await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/compro/organizations/${id}`);
            const completionStore = useCompanyProfileCompletionState.getState();
            completionStore.fetchCompletion();
            toast.success('Organization deleted successfully');
            set(state => ({
                organizations: state.organizations.filter(org => org.id !== id),
                isLoading: false
            }));
        } catch (error) {
            errorHandler(error);
            set({ isLoading: false, error });
        }
    },

    setPublished: async (id, tenantId, isPublished) => {
        set({ isLoading: true });
        try {
            await axiosInstance.put(`${env.apiTasklistBaseUrl}/api/v1/compro/organizations/${id}/publish`, {
                is_published: isPublished,
                tenant_id: tenantId
            });

            // Refresh list to reflect changes (since other orgs might be unpublished)
            await get().fetchOrganizations(tenantId);

            toast.success(`Organization ${isPublished ? 'published' : 'unpublished'} successfully`);
        } catch (error) {
            errorHandler(error);
            set({ isLoading: false, error });
        }
    },
}));

export default useOrganizationListState;
